import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleRight,
  faCalendarDays,
  faClose,
} from "@fortawesome/pro-light-svg-icons";
import planeIcon from "../images/plane-icon.png";
import Image from "../components/image";
import { updateDataLayer } from "../utils/helpers";

const Backdrop = styled.button`
  all: unset;
  position: fixed;
  top: var(--modalTop, 0px);
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9998;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
`;

const Dialog = styled.div`
  position: fixed;
  top: var(--modalTop, 0px);
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    align-items: flex-start;
  }
`;

const Modal = styled.div`
  position: relative;
  width: min(1200px, 92vw);
  background: var(--color-white);
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid var(--color-black);
  padding: 5rem;

  /* desktop: begränsa höjd */
  max-height: min(90vh, calc(100dvh - var(--modalTop, 0px) - 16px));
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    min-width: 100%;
    border-radius: 0;
    padding: 2rem 1rem;
    height: calc(100dvh - var(--modalTop, 0px));
    max-height: calc(100dvh - var(--modalTop, 0px));
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    padding-bottom: max(1rem, env(safe-area-inset-bottom, 0px));
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
  color: var(--color-black);
`;

const Title = styled.h2`
  font-weight: 700;
  font-size: 32px;
  svg {
    margin-right: 5px;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 28px;
    margin-block: 0;
  }
`;
const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 1.5rem;
`;
const SubTitle = styled.p`
  font-size: 24px;
  width: fit-content;
  margin: 0;
  white-space: nowrap;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 18px;
  }
`;
const ClinicButton = styled.button`
  border: 1px solid var(--color-black);
  background-color: var(--color-white);
  border-radius: 6px;
  cursor: pointer;
  padding: 1.5rem;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  padding: 1rem 2.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: var(--color-black);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 0.8rem 1rem;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    row-gap: 0.25rem;
  }

  div {
    display: flex;
    align-items: center;
    width: fit-content;
    gap: 6px;
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      justify-content: flex-start;
    }
  }

  strong {
    font-size: 18px;
    font-weight: 700;
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      font-size: 16px;
    }
  }
  p {
    font-size: 16px;
    color: var(--color-primary);
    font-weight: 400;
    margin: 0;
    display: flex;
    align-items: center;
    padding-left: 1.8rem;
    gap: 5px;
    width: fit-content;
    svg {
      margin-top: 3px;
      font-size: 14px;
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      justify-content: flex-start;
      width: 100%;
    }
  }

  @media (hover: hover) {
    &:hover {
      background-color: #eb5d2a29;
      border: 1px solid var(--color-primary);
    }
  }
`;

export const CLINICS = [
  { name: "Tannfeen Bergen", dentliId: 162 },
  { name: "Tannfeen Drammen", dentliId: 164 },
  { name: "Tannfeen Gjøvik", dentliId: 163 },
  { name: "Tannfeen Hamar", dentliId: 160 },
  { name: "Tannfeen Lillestrøm", dentliId: 165 },
  { name: "Tannfeen Moss", dentliId: 158 },
  { name: "Tannfeen Porsgrunn", dentliId: 167 },
  { name: "Tannfeen Sarpsborg", dentliId: 166 },
  { name: "Tannfeen Ski", dentliId: 161 },
  { name: "Tannfeen Tønsberg", dentliId: 159 },
];

export const DENTLI_OBIDS = [
  { obid: 216, clinic: "Moss" },
  { obid: 218, clinic: "Tønsberg" },
  { obid: 219, clinic: "Hamar" },
  { obid: 220, clinic: "Ski" },
  { obid: 221, clinic: "Bergen" },
  { obid: 222, clinic: "Gjøvik " },
  { obid: 223, clinic: "Drammen" },
  { obid: 224, clinic: "Lillestrøm " },
  { obid: 225, clinic: "Sarpsborg" },
  { obid: 226, clinic: "Porsgrunn" },
];

const BookingModalContext = createContext(null);

export const useBookingModal = () => {
  const ctx = useContext(BookingModalContext);
  if (!ctx)
    throw new Error("useBookingModal must be used within BookingModalProvider");
  return ctx;
};

export const BookingModalProvider = ({ children }) => {
  const [isOpen, setOpen] = useState(false);

  const open = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, close]);

  return (
    <BookingModalContext.Provider value={{ isOpen, open, close }}>
      {children}
    </BookingModalContext.Provider>
  );
};

BookingModalProvider.propTypes = {
  children: PropTypes.node,
};

BookingModalProvider.defaultProps = {
  children: null,
};

export const BookingModalRoot = () => {
  if (typeof document === "undefined") return null;
  const root = document.getElementById("modal-root") || document.body;
  const { isOpen, close } = useBookingModal();

  return ReactDOM.createPortal(
    <>
      <Backdrop
        aria-label="Stäng modal"
        onClick={close}
        style={{ display: isOpen ? "block" : "none" }}
      />
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-label="Bokning"
        style={{ display: isOpen ? "flex" : "none" }}
      >
        <Modal>
          <CloseBtn aria-label="Stäng" onClick={close}>
            <FontAwesomeIcon icon={faClose} />
          </CloseBtn>
          <TitleContainer>
            <Title>
              <FontAwesomeIcon icon={faCalendarDays} /> Bestill time
            </Title>
            <SubTitle>Velg klinikk</SubTitle>
          </TitleContainer>
          <BookingForm onClose={close} clinics={CLINICS} />
        </Modal>
      </Dialog>
    </>,
    root
  );
};

const BookingForm = ({ clinics, onClose }) => {
  const sendEvent = (clinicName) => {
    updateDataLayer(async () =>
      window.dataLayer.push({
        event: "clinic_click",
        clinic_name: clinicName,
      })
    );
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: 12,
        marginTop: 8,
      }}
    >
      {clinics.map((c) => (
        <ClinicButton
          key={String(c.dentliId)}
          type="button"
          className="dentli_booking_container"
          data-dentli_ob_mode="modal"
          data-dentli_ob_id={c.dentliId}
          onClick={() => {
            setTimeout(onClose, 0);
            sendEvent(c.name);
          }}
        >
          <div>
            <Image itemProp="image" src={planeIcon} alt="plane" />
            <strong>{c.name}</strong>
          </div>
          <p>
            Bestill time <FontAwesomeIcon icon={faAngleRight} />
          </p>
        </ClinicButton>
      ))}
    </div>
  );
};

BookingForm.propTypes = {
  clinics: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      dentliId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ),
  onClose: PropTypes.func.isRequired,
};

BookingForm.defaultProps = {
  clinics: [],
};

export default BookingModalContext;
